// PersonaPanel — read / edit / version-history / approve-reject for one agent's
// persona (system prompt). Backed by migration 0043 (agent_personas +
// agent_prompt_versions). Owner writes flow through the SECURITY DEFINER RPCs
// agent_persona_save_human_edit / agent_persona_decide; renames + the
// "suggest improvement" request are owner UPDATEs on agent_personas.
//
// Presentation + governance: this edits the persona Joel sees + governs; it does
// not (yet) drive the daemons' in-code prompts.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, History, Loader2, Pencil, Sparkles, Wand2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNowStrict } from 'date-fns';
import { supabase } from '@/lib/supabase';
import PromptDiff from './PromptDiff';

type PersonaRow = {
  agent_key: string;
  display_name: string;
  self_improve_enabled: boolean;
  suggestion_status: 'idle' | 'requested' | 'generating' | 'ready' | 'error';
  suggestion_requested_at: string | null;
  suggestion_error: string | null;
};

type VersionRow = {
  id: string;
  agent_key: string;
  version: number;
  system_prompt: string;
  summary: string | null;
  rationale: string | null;
  status: 'active' | 'proposed' | 'rejected' | 'superseded';
  origin: 'seed' | 'human' | 'ai';
  decided_at: string | null;
  created_at: string;
};

const ORIGIN_LABEL: Record<VersionRow['origin'], string> = {
  seed: 'Initial',
  human: 'You',
  ai: 'AI',
};

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export default function PersonaPanel({
  agentKey,
  fallbackLabel,
  open,
  onOpenChange,
}: {
  agentKey: string;
  fallbackLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [diffVersionId, setDiffVersionId] = useState<string | null>(null);

  const personaQ = useQuery({
    queryKey: ['agent_persona', agentKey],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('agent_personas')
        .select(
          'agent_key, display_name, self_improve_enabled, suggestion_status, suggestion_requested_at, suggestion_error',
        )
        .eq('agent_key', agentKey)
        .maybeSingle();
      if (error) throw error;
      return (data as PersonaRow | null) ?? null;
    },
  });

  const versionsQ = useQuery({
    queryKey: ['agent_prompt_versions', agentKey],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('agent_prompt_versions')
        .select(
          'id, agent_key, version, system_prompt, summary, rationale, status, origin, decided_at, created_at',
        )
        .eq('agent_key', agentKey)
        .order('version', { ascending: false });
      if (error) throw error;
      return (data ?? []) as VersionRow[];
    },
  });

  const versions = versionsQ.data ?? [];
  const active = versions.find((v) => v.status === 'active') ?? null;
  const proposed = versions.find((v) => v.status === 'proposed') ?? null;
  const history = versions.filter((v) => v.id !== active?.id && v.id !== proposed?.id);

  // Reset all transient UI when the dialog closes (and whenever the agent
  // changes) so a half-typed draft from one agent never leaks into another.
  useEffect(() => {
    if (!open) {
      setEditing(false);
      setRenaming(false);
      setDiffVersionId(null);
      setDraft('');
      setEditSummary('');
    }
  }, [open, agentKey]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['agent_persona', agentKey] });
    void qc.invalidateQueries({ queryKey: ['agent_prompt_versions', agentKey] });
  };

  const saveEdit = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('agent_persona_save_human_edit', {
        p_agent_key: agentKey,
        p_system_prompt: draft.trim(),
        p_summary: editSummary.trim() || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      setEditing(false);
      setEditSummary('');
      toast.success('Persona updated', { description: 'Saved as a new version.' });
    },
    onError: (e) => toast.error('Could not save persona', { description: msg(e) }),
  });

  const decide = useMutation({
    mutationFn: async (decision: 'approve' | 'reject') => {
      if (!proposed) return;
      const { error } = await supabase.rpc('agent_persona_decide', {
        p_version_id: proposed.id,
        p_decision: decision,
      });
      if (error) throw error;
    },
    onSuccess: (_d, decision) => {
      invalidate();
      setDiffVersionId(null);
      toast.success(decision === 'approve' ? 'Suggestion approved' : 'Suggestion rejected');
    },
    onError: (e) => toast.error('Could not record decision', { description: msg(e) }),
  });

  const requestSuggest = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('agent_personas')
        .update({
          suggestion_requested_at: new Date().toISOString(),
          suggestion_status: 'requested',
        })
        .eq('agent_key', agentKey);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Improvement requested', {
        description: 'The Learning Loop will draft a suggestion for you to review.',
      });
    },
    onError: (e) => toast.error('Could not request a suggestion', { description: msg(e) }),
  });

  const updatePersona = useMutation({
    mutationFn: async (
      patch: Partial<Pick<PersonaRow, 'display_name' | 'self_improve_enabled'>>,
    ) => {
      const { error } = await supabase
        .from('agent_personas')
        .update(patch)
        .eq('agent_key', agentKey);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      setRenaming(false);
    },
    onError: (e) => toast.error('Could not update', { description: msg(e) }),
  });

  const persona = personaQ.data;
  const title = persona?.display_name ?? fallbackLabel;
  const suggesting =
    persona?.suggestion_status === 'requested' || persona?.suggestion_status === 'generating';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <Sparkles className="w-4 h-4 text-forest shrink-0" aria-hidden />
            {renaming ? (
              <span className="flex items-center gap-1.5">
                <Input
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  className="h-7 w-56"
                  autoFocus
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  disabled={!nameDraft.trim() || updatePersona.isPending}
                  onClick={() => updatePersona.mutate({ display_name: nameDraft.trim() })}
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => setRenaming(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </span>
            ) : (
              <DialogTitle className="flex items-center gap-1.5">
                {title}
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 text-muted-foreground"
                  onClick={() => {
                    setNameDraft(title);
                    setRenaming(true);
                  }}
                  aria-label="Rename agent"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
              </DialogTitle>
            )}
            {persona &&
              (persona.self_improve_enabled ? (
                <Badge variant="secondary" className="text-[10px] bg-forest-soft text-forest">
                  Self-improving
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] text-muted-foreground">
                  Self-improve off
                </Badge>
              ))}
            {active && (
              <span className="text-[11px] text-muted-foreground font-mono">v{active.version}</span>
            )}
          </div>
          <DialogDescription>
            This is the persona Joel sees and governs. Edits are version-tracked; AI suggestions
            wait for your approval.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-1 px-1">
          <div className="space-y-4">
            {/* Pending AI proposal */}
            {proposed && active && (
              <div className="rounded-xl border border-forest/30 bg-forest-soft/40 p-3.5">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-forest">
                    <Wand2 className="w-4 h-4" aria-hidden /> AI suggestion pending review
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    proposed{' '}
                    {formatDistanceToNowStrict(new Date(proposed.created_at), { addSuffix: true })}
                  </span>
                </div>
                {proposed.rationale && (
                  <p className="text-xs text-muted-foreground mb-2 italic">
                    “{proposed.rationale}”
                  </p>
                )}
                <div className="rounded-lg bg-background/60 p-3">
                  <PromptDiff before={active.system_prompt} after={proposed.system_prompt} />
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Button
                    size="sm"
                    className="bg-forest hover:bg-forest/90"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate('approve')}
                  >
                    {decide.isPending && decide.variables === 'approve' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate('reject')}
                  >
                    {decide.isPending && decide.variables === 'reject' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                    Reject
                  </Button>
                </div>
              </div>
            )}

            {/* Current persona / edit */}
            <div>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Current system prompt
                </span>
                {!editing && active && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-forest"
                    onClick={() => {
                      setDraft(active.system_prompt);
                      setEditSummary('');
                      setEditing(true);
                    }}
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Button>
                )}
              </div>

              {editing ? (
                <div className="space-y-2">
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={8}
                    className="text-sm leading-relaxed"
                  />
                  <Input
                    value={editSummary}
                    onChange={(e) => setEditSummary(e.target.value)}
                    placeholder="What changed? (optional one-liner)"
                    className="h-8 text-sm"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="bg-forest hover:bg-forest/90"
                      disabled={!draft.trim() || saveEdit.isPending}
                      onClick={() => saveEdit.mutate()}
                    >
                      {saveEdit.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      Save new version
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">
                  {active
                    ? active.system_prompt
                    : versionsQ.isLoading
                      ? 'Loading…'
                      : 'No persona yet.'}
                </p>
              )}
            </div>

            {/* Self-improvement controls */}
            {!editing && (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-border-soft/60 p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">Self-improvement</p>
                  <p className="text-xs text-muted-foreground">
                    Let the Learning Loop propose improvements from this agent's outcomes — you
                    always approve.
                  </p>
                </div>
                <Switch
                  checked={persona?.self_improve_enabled ?? true}
                  disabled={personaQ.isLoading || updatePersona.isPending}
                  onCheckedChange={(v) => updatePersona.mutate({ self_improve_enabled: v })}
                />
              </div>
            )}

            {!editing && (
              <div className="space-y-1.5">
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={
                    personaQ.isLoading ||
                    requestSuggest.isPending ||
                    suggesting ||
                    !!proposed ||
                    !(persona?.self_improve_enabled ?? true)
                  }
                  onClick={() => requestSuggest.mutate()}
                >
                  {requestSuggest.isPending || suggesting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Drafting a suggestion…
                    </>
                  ) : proposed ? (
                    'A suggestion is awaiting your review above'
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" /> Suggest an improvement
                    </>
                  )}
                </Button>
                {persona?.suggestion_status === 'error' && (
                  <p className="text-[11px] text-alert">
                    Last suggestion failed
                    {persona.suggestion_error ? `: ${persona.suggestion_error}` : ''}. You can try
                    again.
                  </p>
                )}
              </div>
            )}

            {/* Version history */}
            {history.length > 0 && (
              <div>
                <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                  <History className="w-3.5 h-3.5" /> Version history
                </span>
                <div className="space-y-1.5">
                  {history.map((v) => (
                    <div key={v.id} className="rounded-lg border border-border-soft/50 px-3 py-2">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-2 text-left"
                        onClick={() => setDiffVersionId(diffVersionId === v.id ? null : v.id)}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-xs text-muted-foreground">
                            v{v.version}
                          </span>
                          <span className="text-sm text-foreground/80 truncate">
                            {v.summary ?? 'No summary'}
                          </span>
                        </span>
                        <span className="flex items-center gap-2 shrink-0">
                          <Badge variant="secondary" className="text-[10px]">
                            {ORIGIN_LABEL[v.origin]}
                          </Badge>
                          <span className="text-[10px] uppercase text-muted-foreground">
                            {v.status}
                          </span>
                        </span>
                      </button>
                      {diffVersionId === v.id && active && (
                        <div className="mt-2 rounded-md bg-background/60 p-2.5">
                          <p className="text-[11px] text-muted-foreground mb-1.5">
                            Changes from v{v.version} → current v{active.version}:
                          </p>
                          <PromptDiff before={v.system_prompt} after={active.system_prompt} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
